import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Upload, FileDown } from "lucide-react";

interface NotesSectionProps {
  data: {
    general_notes: string;
    has_transcript: boolean;
    transcript_text: string;
    transcript_file_url: string;
  };
  onChange: (updates: any) => void;
  isFirstMeeting?: boolean;
}

export function NotesSection({ data, onChange, isFirstMeeting }: NotesSectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-4">General Notes & Transcript</h2>
        <p className="text-muted-foreground">
          Record general observations and attach meeting transcripts if available.
        </p>
      </div>

      {/* First Meeting Helper Banner */}
      {isFirstMeeting && (
        <Card className="p-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="font-medium text-green-800 dark:text-green-200">
                ✓ Meeting Summary Pre-Written
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                A general summary of your first organizational meeting has been pre-filled below.
                You can edit it if needed, or leave it as is. No transcript is required for this meeting.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* General Notes */}
      <Card className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          <h3 className="font-semibold">General Notes</h3>
        </div>

        <div className="space-y-2">
          <Label htmlFor="general-notes">Meeting Notes</Label>
          <Textarea
            id="general-notes"
            value={data.general_notes}
            onChange={(e) => onChange({ general_notes: e.target.value })}
            placeholder="Write down anything worth noting that does not fit the other sections..."
            rows={10}
            className="min-h-[200px]"
          />
          <p className="text-xs text-muted-foreground">
            Use this space for observations about meeting dynamics, attendance issues, or other important context.
          </p>
        </div>
      </Card>

      {/* Transcript Section */}
      <Card className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          <h3 className="font-semibold">Meeting Transcript</h3>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="has-transcript"
            checked={data.has_transcript}
            onCheckedChange={(checked) => onChange({ has_transcript: checked })}
          />
          <Label htmlFor="has-transcript" className="cursor-pointer">
            Meeting was recorded or transcribed
          </Label>
        </div>

        {data.has_transcript && (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="transcript-url">Transcript File URL</Label>
              <div className="flex gap-2">
                <Input
                  id="transcript-url"
                  type="url"
                  value={data.transcript_file_url}
                  onChange={(e) => onChange({ transcript_file_url: e.target.value })}
                  placeholder="https://example.com/transcript.pdf"
                />
                {data.transcript_file_url && (
                  <a
                    href={data.transcript_file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 border rounded-md hover:bg-muted transition-colors"
                  >
                    <FileDown className="h-4 w-4" />
                  </a>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Link to uploaded transcript file or recording
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transcript-text">Or Paste Transcript Text</Label>
              <Textarea
                id="transcript-text"
                value={data.transcript_text}
                onChange={(e) => onChange({ transcript_text: e.target.value })}
                placeholder="Paste the full transcript of the meeting here..."
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Full verbatim transcript if available. This will be attached to the final minutes document.
              </p>
            </div>
          </div>
        )}
      </Card>

      <div className="bg-muted/50 border border-border rounded-lg p-4">
        <p className="text-sm text-muted-foreground">
          <strong>Note:</strong> General notes and transcripts provide important context but are separate from the formal minutes. 
          The minutes will focus on decisions, motions, and action items, while this section captures additional details.
        </p>
      </div>
    </div>
  );
}
